<template>
    <div class="modal-full">
        <div class="modal-header">
            <close-icon v-on:click="closeView"></close-icon>
            <h1>Edit Event</h1>
        </div>

        <div class="modal-body no-pad">
            <div class="flex h-full">
                <div class="account-sidebar h-full">
                    <div class="mb-4 text-center">
                        <span class="fa-layers fa-4x mb-3">
                            <i class="fa fa-circle"></i>
                            <i class="fas fa-user fa-inverse" data-fa-transform="shrink-6.5"></i>
                        </span>

                        <p class="text-lg font-light">Event</p>
                    </div>

                    <div class="mb-3" v-if="event">
                        <label>Event ID</label>
                        <p>{{ event.id }}</p>

                        <label>Published</label>
                        <p>{{ event.published ? 'yes' : 'no' }}</p>

                        <label>Date Submitted</label>
                        <p>{{ formatDateShort(event.created_at) }}</p>
                    </div>
                </div>

                <div class="account-body">
                    <form class="profile-form" v-if="event">
                        <div class="form-field">
                            <label>Event Title</label>
                            <input type="text" v-model="event.title">
                        </div>

                        <div class="form-field">
                            <label>Event Description</label>

                            <Editor
                                :init="{...wysiwygOptions,
                                    plugins: wysiwygPlugins,
                                    height: 250,
                                }"
                                api-key="kxkhkjfta11crbajh0in2mcap04z7nt4czo9icice5ihpd8n"
                                v-model="event.description"
                            >
                            </Editor>
                        </div>

                        <div class="form-field">
                            <label>Image</label>
                            <image-drop
                                instance="Event"
                                class="w-full sm:w-1/2 md:w-1/4"
                                :instance-id="event.id"
                                name="image"
                                :upload-url="imageUploadUrl"
                                :value="event.image_file"
                                target="image"
                                @input="handleImageUpdate"
                            ></image-drop>
                        </div>

                        <div class="form-field">
                            <label>Start Date and Time</label>
                            <template v-if="isPastEvent(event.start_date)">
                                <input type="text" :placeholder="getPastEventDate(event.start_date)" disabled>
                                <small>This date is in the past. Click duplicate to proceed</small>
                            </template>
                            <template v-else>

                                <simple-date-time-picker
                                    v-model="event.start_date">
                                </simple-date-time-picker>
                            </template>
                        </div>

                        <div class="form-field">
                            <label>End Date and Time</label>
                            <template v-if="isPastEvent(event.end_date)">
                                <input type="text" :placeholder="getPastEventDate(event.end_date)" disabled>
                                <small>This date is in the past. Click duplicate to proceed</small>
                            </template>
                            <template v-else>

                                <simple-date-time-picker
                                    v-model="event.end_date">
                                </simple-date-time-picker>
                            </template>
                        </div>

                        <div class="form-field">
                            <label>All Day</label>
                            <CheckBox v-model="event.all_day"/>
                        </div>

                        <div class="form-field">
                            <label>Publish</label>
                            <select v-model="event.published">
                                <option :value="0">No</option>
                                <option :value="1">Yes</option>
                            </select>
                        </div>

                        <div class="form-field">
                            <label>Category</label>
                            <select-list
                                placeholder="Select Category"
                                route="event.categories.list"
                                value-key="id"
                                option-key="title"
                                @selected="prepareCategory"
                                class="appearance-none border w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none focus:shadow-outline"
                                :selected-option="event.event_category_id"
                            ></select-list>
                        </div>

                        <div class="form-field">
                            <label>Sector</label>
                            <picker-selector
                                v-if="sectors.length > 0"
                                placeholder="Select Sectors"
                                v-model="eventSectors"
                                input-name="sectors[]"
                                @input="addSectors"
                                :options="sectors">
                            </picker-selector>
                        </div>

                        <div class="form-field">
                            <label>Location</label>
                            <picker-selector
                                v-if="locations.length > 0"
                                placeholder="Select Locations"
                                v-model="eventLocations"
                                input-name="locations[]"
                                @input="addLocations"
                                :options="locations">
                            </picker-selector>
                        </div>

                    </form>
                </div>
            </div>
        </div>

        <div class="modal-footer">
            <div class="flex">
                <div class="left text-left">
                    <button type="button" class="btn btn-primary" @click="duplicateEvent">Duplicate Event</button>
                    <button class="btn-danger muted" type="button" @click="handleDeleteEvent">Delete Event</button>
                </div>

                <div class="right text-right">
                    <button class="btn-tertiary muted" type="button" @click="closeView">Close</button>
                    <button class="btn-primary" type="button" @click="saveForm">Save</button>
                </div>
            </div>
        </div>
        <router-view v-slot="{ Component }">
            <transition name="drop-in" mode="in-out">
                <component :is="Component"></component>
            </transition>
        </router-view>
    </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'
import {useStore} from "vuex"
import moment from "moment"

export default {
    name: 'EventView',
    props: {
        //
    },
    data() {
        return {
            event: null,
            eventLocations: null,
            eventSectors: null
        }
    },
    components: {
        Editor,
    },
    computed: {
        sectors () {
            const store = useStore()
            return store.state.sectors ?? []
        },
        locations () {
            const store = useStore()
            return store.state.locations ?? []
        },
        wysiwygOptions() {
            return {
                statusbar: false,
                menubar: false,
                toolbar: 'undo redo | bold italic underline | link',
                formats: {},
                height: 200,
            }
        },
        wysiwygPlugins() {
            return ['link']
        },
        imageUploadUrl() {
            return route('file.upload', {
                'instance': 'Event',
                'instanceId': this.event.id
            })
        },
        datepickerType() {
            if (this.event.all_day) {
                return 'date'
            }
            return 'datetime'
        },
        datepickerFormat() {
            switch (this.datepickerType) {
                case 'date':
                    return 'ccc dd LLL yyyy'
                case 'datetime':
                    return 'ccc dd LLL yyyy HH:mm'
            }
        },
    },
    mounted() {
        this.setMetaTitle('View Event | JournoLink Control')
        this.loadData()
    },
    methods: {
        loadData() {
            axios.get(route('event.show', this.$route.params.eventId))
                .then(response => {
                    console.log(response.data)
                    this.event = response.data.event
                    this.addSectors(response.data.event.targets.sector)
                    this.addLocations(response.data.event.targets.location)
                })
                .catch(e => {
                    console.error({e})
                })
        },

        /**
         * Emit the close event to the parent
         *
         * @return {void}
         */
        closeView() {
            this.setMetaTitle('List Events | JournoLink Control')
            this.$root.goBack()
        },
        /**
         *
         */
        saveForm() {
            axios.put(route('event.update', this.$route.params.eventId),
                {
                    event: this.formatEventForSave(this.event),
                    sectors: _.castArray(this.eventSectors),
                    locations: _.castArray(this.eventLocations),
                })
                .then(response => {
                    this.event = response.data.event
                    this.$bus.$emit('alert.success', 'The Event details have been saved')
                })
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Handle an [Escape] keypress
         *
         * @return {void}
         */
        onEscapeKeypress() {
            this.closeView()
        },
        /**
         * set the category id
         * @param  {Number} categoryID
         * @return {void}
         */
        prepareCategory(categoryID) {
            this.event.event_category_id = categoryID
        },
        /**
         * Format the Event object for save
         *
         * @param  {Object} event
         * @return {Object}
         */
        formatEventForSave(event) {
            let thisEvent = _.clone(event)
            thisEvent.start_date = thisEvent.start_date && moment(thisEvent.start_date).format('YYYY-MM-DD HH:mm:ss')
            thisEvent.end_date = thisEvent.end_date && moment(thisEvent.end_date).format('YYYY-MM-DD HH:mm:ss')

            return thisEvent
        },
        /**
         *
         */
        handleDeleteEvent() {
            this.alert.fire({
                title: 'Are you sure?',
                text: 'This will delete the Event. This action is irreversible.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#E3342F',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.delete(route('event.delete', this.$route.params.eventId), {}).then(response => {
                            this.$bus.$emit('alert.success', 'Event Deleted')
                            this.$router.push({name: 'event.list'})
                        }).catch(e => {
                            console.error({e})
                            this.$bus.$emit('alert.danger', 'Event Not Deleted')
                        })
                    }
                })
        },
        addLocations(ids) {
            this.eventLocations = ids
        },
        addSectors(ids) {
            this.eventSectors = ids
        },
        /**
         * Handle a event image upload
         *
         * @param  {Object} image
         * @return {void}
         */
        handleImageUpdate() {
            axios.get(route('file.get', {
                'instance': 'Event',
                'instanceId': this.event.id
            })).then(response => {
                this.event.image_file = response.data[0]
            }).catch((e) => {
                console.error(e)
            })
        },
        isPastEvent(eventDate) {
            return moment(eventDate).isBefore(moment())
        },
        getPastEventDate(eventDate) {
            return moment(eventDate).format('DD/MM/YYYY')
        },
        duplicateEvent() {
            this.$router.push({
                name: 'event.create', params: { existingEvent: this.event.id },
            })
        }
    },
}
</script>

<style lang="less" scoped>
.account-sidebar {
    padding: 15px;
    width: 350px;
    font-size: 0.9rem;
    background: #f6f6f6;

    label {
        font-weight: bold;
        font-size: 0.8em;
        color: #999999;
    }

    p {
        margin-bottom: 0.5em;
    }
}

.account-body {
    width: calc(100% - 350px);
}

.account-activity {
    width: 250px;
    font-size: 0.9rem;
    padding: 15px;
}
</style>
